import { useContext, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom';
import * as Actions from 'flex/Actions';
import { checkEnvironmentForControllable, generateApiDomain } from 'constants/GlobalConfig';
import { LocationContext } from 'App';

/**
 * 学校管理画面ではMFA登録を行わない仕様のため、MFA未登録ユーザーには
 * 登録画面の代わりにエラーモーダル（Message コンポーネント）を表示するためのペイロード
 */
const generateMfaUnregisteredFailurePayload = () => ({
    result: {
        message: '多要素認証が登録されていません',
        errors: [
            '学校管理画面にログインするには多要素認証の設定が必要です。',
            'ホーム画面の右上のメニューから認証アプリの設定を行ってください。',
        ],
    },
    details: {
        date: new Date(),
        baseURL: '',
        url: 'なし',
        trackingInfo: '',
        userAgent: navigator.userAgent,
        frontAppVersion: '1.0.0',
    },
});

export default function AuthEffectComponent() {
    const [previewLocation] = useContext(LocationContext);
    const history = useHistory();
    const checkLoginConnection = useSelector(state => state.checkLoginConnection);
    const loginConnection = useSelector(state => state.loginConnection);
    const logoutConnection = useSelector(state => state.logoutConnection);
    const tenantConnection = useSelector(state => state.tenantConnection);
    const dispatch = useDispatch();

    const isFirstCheckRender = useRef(false)

    useEffect(() => {
        isFirstCheckRender.current = true;
    }, []);

    useEffect(() => {
        if (isFirstCheckRender.current) {
            if (checkLoginConnection.meta.status === Actions.statusEnum.REQUEST) {
                dispatch(Actions.data.session(false));
            }
        }
        if (checkLoginConnection.meta.status === Actions.statusEnum.SUCCESS) {
            const isEnableMFA =
                checkLoginConnection.payload.enable_mfa &&
                checkLoginConnection.payload.require_mfa_permission;

            if (isEnableMFA && checkLoginConnection.payload.require_mfa) {
                if (!checkLoginConnection.payload.user?.registered_mfa) {
                    isFirstCheckRender.current = false;
                    // 学校管理画面ではMFA登録を行わないため、エラーを表示してサインイン画面へ戻す
                    if (!checkEnvironmentForControllable()) {
                        dispatch(Actions.data.failure(generateMfaUnregisteredFailurePayload(), true));
                        dispatch(Actions.data.session(false));
                        if (window.location.pathname !== '/login') {
                            // フルリロードするとエラーモーダルが消えるため、SPA内遷移で戻す
                            history.replace('/login');
                        }
                        return;
                    }
                    // MFA未登録 → MFA登録ページへリダイレクト（セッション設定前に即時遷移）
                    if (window.location.pathname !== '/verify') {
                        window.location.replace(`/verify`);
                    }
                    return;
                }
                if (!checkLoginConnection.payload.user?.verified_mfa) {
                    // MFA登録済みだが未認証 → MFA認証ページへリダイレクト
                    isFirstCheckRender.current = false;
                    const loginInputCodePath = checkEnvironmentForControllable()
                        ? '/control/two-factor-auth/login-input-code'
                        : '/two-factor-auth/login-input-code';
                    // MFA認証後、認証に飛ぶ直前のページ（パス・クエリ含む）へ戻すためリダイレクト先を渡す
                    const redirectQuery = `?redirect=${encodeURIComponent(window.location.href)}`;
                    window.location.replace(`${generateApiDomain(true)}${loginInputCodePath}${redirectQuery}`);
                    return;
                }
            }

            dispatch(Actions.data.session(checkLoginConnection.payload.is_successful));
            dispatch(Actions.data.user(checkLoginConnection.payload.is_successful ? checkLoginConnection.payload.user : null));
            dispatch(Actions.data.tenant(checkLoginConnection.payload.result.tenant));
            dispatch(Actions.data.isEnableMFA(isEnableMFA));
            dispatch(Actions.data.isEnableTenantMFA(!!checkLoginConnection.payload.enable_mfa));
            // どのページでも利用され、どのユーザでも、頻繁に変更されるものではないため、ここで取得しておく
            dispatch(Actions.http.connection.term.search({ page_size: '50' }, undefined, 'globalState'));
            isFirstCheckRender.current = false;
        }
        if (checkLoginConnection.meta.status === Actions.statusEnum.FAILURE) {
            if (previewLocation !== "/login") {
                //loginページからではないので、該当画面に遷移させる
                if (!checkEnvironmentForControllable()) {
                    if (checkLoginConnection.payload.result?.login_url) {
                        window.location.replace(`${generateApiDomain(true)}${checkLoginConnection.payload.result.login_url}`)
                    } else {
                        //local接続時はlogin_urlが帰ってこないため、サインインページに遷移
                        window.location.replace(`/login`);
                    }
                } else {
                    window.location.replace(`/login`);
                }
            } else {
                //何もしない
            }
            dispatch(Actions.data.session(false));
            isFirstCheckRender.current = false;
        }
        if (checkLoginConnection.meta.status === Actions.statusEnum.ERROR) {
            //想定しないエラー
            isFirstCheckRender.current = false;
        }
    }, [checkLoginConnection, dispatch]);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (loginConnection.meta.status === Actions.statusEnum.SUCCESS && loginConnection.payload.is_successful === true) {
            const isEnableMFA =
                loginConnection.payload.enable_mfa &&
                loginConnection.payload.require_mfa_permission;

            if (isEnableMFA && loginConnection.payload.require_mfa) {
                if (!loginConnection.payload.user?.registered_mfa) {
                    // 学校管理画面ではMFA登録を行わないため、エラーを表示してサインイン失敗として扱う
                    if (!checkEnvironmentForControllable()) {
                        dispatch(Actions.data.failure(generateMfaUnregisteredFailurePayload(), true));
                        dispatch(Actions.data.session(false));
                        return;
                    }
                    // MFA未登録 → MFA登録ページへリダイレクト
                    if (window.location.pathname !== '/verify') {
                        window.location.replace(`/verify`);
                    }
                    return;
                }
                if (!loginConnection.payload.user?.verified_mfa) {
                    // MFA登録済みだが未認証 → MFA認証ページへリダイレクト
                    const loginInputCodePath = checkEnvironmentForControllable()
                        ? '/control/two-factor-auth/login-input-code'
                        : '/two-factor-auth/login-input-code';
                    // MFA認証後、認証に飛ぶ直前のページ（パス・クエリ含む）へ戻すためリダイレクト先を渡す
                    const redirectQuery = `?redirect=${encodeURIComponent(window.location.href)}`;
                    window.location.replace(`${generateApiDomain(true)}${loginInputCodePath}${redirectQuery}`);
                    return;
                }
            }

            //サインイン完了時
            dispatch(Actions.data.session(true));
            dispatch(Actions.data.user(loginConnection.payload.user));
            dispatch(Actions.data.isEnableMFA(isEnableMFA));
            dispatch(Actions.data.isEnableTenantMFA(!!loginConnection.payload.enable_mfa));
            //バックグラウンドでテナントに関する情報を取得
            dispatch(Actions.http.connection.authentication.tenant());
            // どのページでも利用され、どのユーザでも、頻繁に変更されるものではないため、ここで取得しておく
            dispatch(Actions.http.connection.term.search({ page_size: '50' }, undefined, 'globalState'));
        }
        if (loginConnection.meta.status === Actions.statusEnum.FAILURE || loginConnection.meta.status === Actions.statusEnum.ERROR) {
            dispatch(Actions.data.session(false));
        }
    }, [loginConnection, dispatch]);


    useEffect(() => {
        if (logoutConnection.meta.status === Actions.statusEnum.SUCCESS) {
            dispatch(Actions.data.session(false));
            dispatch(Actions.data.user(null));
        }
        if (logoutConnection.meta.status === Actions.statusEnum.FAILURE || logoutConnection.meta.status === Actions.statusEnum.ERROR) {
            //OPTIMIZE ここ、怪しいな･･････
            dispatch(Actions.data.session(false));
            dispatch(Actions.data.user(null));
        }
    }, [logoutConnection, dispatch]);


    useEffect(() => {
        if (tenantConnection.meta.status === Actions.statusEnum.SUCCESS) {
            dispatch(Actions.data.tenant(
                tenantConnection.payload.result
            ))
        }
    }, [tenantConnection, dispatch]);


    return null;
}
